import { memo, useEffect, useMemo } from 'react';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { MODAL_CONFIG } from './Modal.content';
import { ModalProps } from './Modal.types';
import useBreakpointProp from '../../hooks/useBreakpointProp';
import ModalOverlay from './ModalOverlay/ModalOverlay';
import ModalHeader from './ModalHeader/ModalHeader';
import Box from '../Box/Box';
import { BackgroundTokens } from '../../theme/index.types';

function Modal({
  id,
  testID,
  title,
  titleNS,
  titleKey,
  hideHeader,
  variant,
  closing,
  canClose,
  animated = true,
  children,
  style,
  close,
  onClosed,
}: ModalProps) {
  const { getAnimatedStyle, ...restConfig } = useBreakpointProp(MODAL_CONFIG);
  const animatedValue = useSharedValue(0.1);
  const animatedStyle = useAnimatedStyle(
    () => getAnimatedStyle(animatedValue),
    [getAnimatedStyle],
  );

  useEffect(() => {
    const config = animated ? undefined : { duration: 0 };

    animatedValue.value = withTiming(Number(!closing), config, () => {
      if (closing && onClosed) {
        runOnJS(onClosed)(id);
      }
    });
  }, [id, closing, animated, animatedValue, onClosed]);

  const backgroundColor = useMemo((): BackgroundTokens => {
    switch (variant) {
      case 'transparent':
        return 'transparent';
      case 'default':
      default:
        return 'screenBackground';
    }
  }, [variant]);

  return (
    <Box absoluteFill centerContent>
      <ModalOverlay onPress={canClose ? close : undefined} />
      <Box
        pointerEvents="auto"
        backgroundColor={backgroundColor}
        animatedStyle={animatedStyle}
        testID={testID}
        {...restConfig}
        {...style}
      >
        {!hideHeader && (
          <ModalHeader
            title={title}
            titleNS={titleNS}
            titleKey={titleKey}
            canClose={canClose}
            close={close}
          />
        )}
        {children}
      </Box>
    </Box>
  );
}

export default memo(Modal);
